import React, { useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { uploadKYCFile, submitKYC } from '../../../api/services/authService';
import './TenantKYCModal.css';

const TenantKYCModal = ({ open, onClose, userId, onSubmitted }) => {
  const [kycDocuments, setKycDocuments] = useState({
    idType: 'NATIONAL_ID'
  });
  
  const [kycUploadState, setKycUploadState] = useState({
    currentUpload: 'idFront', // 'idFront', 'idBack', 'selfie', or null
    uploadedUrls: {
      idFront: null,
      idBack: null,
      selfie: null
    },
    isUploading: false
  });

  const [isSubmittingKYC, setIsSubmittingKYC] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [portalRoot, setPortalRoot] = useState(null);

  useEffect(() => {
    if (!open) {
      setKycUploadState({
        currentUpload: 'idFront',
        uploadedUrls: { idFront: null, idBack: null, selfie: null },
        isUploading: false
      });
      setMessage({ text: '', type: '' });
      setIsSubmittingKYC(false);
      setKycDocuments({ idType: 'NATIONAL_ID' });
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPortalRoot(null);
      return undefined;
    }
    if (typeof document === 'undefined' || !document.body) {
      return undefined;
    }
    const el = document.createElement('div');
    el.setAttribute('data-tenant-kyc-modal-portal', '');
    document.body.appendChild(el);
    setPortalRoot(el);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      setPortalRoot(null);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleKYCUpload = async (file) => {
    if (!file) return;
    
    const currentDoc = kycUploadState.currentUpload;
    if (!currentDoc) {
      setMessage({ text: 'All documents have been uploaded. Please submit KYC.', type: 'error' });
      return;
    }
    
    if (!userId) {
      setMessage({ text: 'User details not found. Please try again later.', type: 'error' });
      return;
    }
    
    setKycUploadState(prev => ({ ...prev, isUploading: true }));
    setMessage({ text: '', type: '' });
    
    try {
      setMessage({ text: `Uploading ${currentDoc === 'idFront' ? 'ID Front' : currentDoc === 'idBack' ? 'ID Back' : 'Selfie'}...`, type: 'success' });
      
      const response = await uploadKYCFile(file, userId);
      
      if (response.status === 'success' && response.data?.url) {
        const url = response.data.url;
        
        setKycUploadState(prev => ({
          ...prev,
          uploadedUrls: {
            ...prev.uploadedUrls,
            [currentDoc]: url
          }
        }));
        
        // Determine next document to upload
        let nextDoc = null;
        if (currentDoc === 'idFront') {
          nextDoc = 'idBack';
        } else if (currentDoc === 'idBack') {
          nextDoc = 'selfie';
        } else if (currentDoc === 'selfie') {
          nextDoc = null; // All done
        }
        
        setKycUploadState(prev => ({
          ...prev,
          currentUpload: nextDoc,
          isUploading: false
        }));
        
        setMessage({ 
          text: `${currentDoc === 'idFront' ? 'ID Front' : currentDoc === 'idBack' ? 'ID Back' : 'Selfie'} uploaded successfully!${nextDoc ? ` Please upload ${nextDoc === 'idBack' ? 'ID Back' : 'Selfie'}.` : ' All documents uploaded. You can now submit KYC.'}`, 
          type: 'success' 
        });
      } else {
        setMessage({ text: response.message || 'Upload failed. Please try again.', type: 'error' });
        setKycUploadState(prev => ({ ...prev, isUploading: false }));
      }
    } catch (error) {
      console.error('KYC upload error:', error);
      setMessage({ text: error.message || 'Upload failed. Please try again.', type: 'error' });
      setKycUploadState(prev => ({ ...prev, isUploading: false }));
    }
  };

  const handleKYCSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });
    
    const { idFront, idBack, selfie } = kycUploadState.uploadedUrls;
    if (!idFront || !idBack || !selfie) {
      setMessage({ text: 'Please upload all documents (ID front, ID back, and selfie) before submitting.', type: 'error' });
      return;
    }
    
    if (!userId) {
      setMessage({ text: 'User details not found. Please try again later.', type: 'error' });
      return;
    }
    
    setIsSubmittingKYC(true);
    
    try {
      setMessage({ text: 'Submitting KYC documents...', type: 'success' });
      
      const response = await submitKYC({
        idType: kycDocuments.idType,
        idDocumentFrontUrl: idFront,
        idDocumentBackUrl: idBack,
        selfieUrl: selfie,
      });
      
      if (response.status === 'success') {
        setMessage({ 
          text: 'KYC documents submitted successfully! Your documents are being reviewed.', 
          type: 'success' 
        });
        
        setTimeout(() => {
          onSubmitted?.();
          onClose();
        }, 2000);
      } else {
        setMessage({ text: response.message || 'Failed to submit KYC documents. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('KYC submission error:', error);
      setMessage({ text: error.message || 'Failed to submit KYC documents. Please try again.', type: 'error' });
    } finally {
      setIsSubmittingKYC(false);
    }
  };

  if (!open || !portalRoot) return null;

  return createPortal(
    <div className="tenant-kyc-modal" role="presentation">
      <button type="button" className="tenant-kyc-modal__backdrop" aria-label="Close" onClick={onClose} />
      <div
        className="tenant-kyc-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tenant-kyc-modal-title"
      >
        <div className="tenant-kyc-modal__header">
          <h2 id="tenant-kyc-modal-title" className="tenant-kyc-modal__title">
            Verify Your Identity (KYC)
          </h2>
          <div className="tenant-kyc-modal__header-actions">
            <button type="button" className="tenant-kyc-modal__close" onClick={onClose} aria-label="Close">
              <MdClose />
            </button>
          </div>
        </div>

        <form className="tenant-kyc-modal__form" onSubmit={handleKYCSubmit}>
          {message.text && (
            <div className={`tenant-kyc-modal__alert tenant-kyc-modal__alert--${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="tenant-kyc-modal__field">
            <label htmlFor="idType" className="tenant-kyc-modal__label">Document Type *</label>
            <select
              id="idType"
              className="tenant-kyc-modal__select"
              value={kycDocuments.idType}
              onChange={(e) => setKycDocuments(prev => ({ ...prev, idType: e.target.value }))}
              required
              disabled={isSubmittingKYC || kycUploadState.currentUpload === null}
            >
              <option value="NATIONAL_ID">National ID</option>
              <option value="PASSPORT">Passport</option>
              <option value="DRIVING_LICENSE">Driving License</option>
            </select>
          </div>

          <div className="tenant-kyc-modal__field">
            <label className="tenant-kyc-modal__label">
              {kycUploadState.currentUpload === 'idFront' && 'Upload ID Front *'}
              {kycUploadState.currentUpload === 'idBack' && 'Upload ID Back *'}
              {kycUploadState.currentUpload === 'selfie' && 'Upload Selfie *'}
              {kycUploadState.currentUpload === null && 'All Documents Uploaded'}
            </label>
            
            <div className={`tenant-kyc-modal__upload-box ${kycUploadState.currentUpload === null ? 'completed' : ''}`}>
              {kycUploadState.currentUpload === null ? (
                <div>
                  <p className="tenant-kyc-modal__upload-success">All documents have been uploaded successfully!</p>
                  <div className="tenant-kyc-modal__upload-status">
                    <div>✓ ID Front: {kycUploadState.uploadedUrls.idFront ? 'Uploaded' : 'Not uploaded'}</div>
                    <div>✓ ID Back: {kycUploadState.uploadedUrls.idBack ? 'Uploaded' : 'Not uploaded'}</div>
                    <div>✓ Selfie: {kycUploadState.uploadedUrls.selfie ? 'Uploaded' : 'Not uploaded'}</div>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    id="kycUpload"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleKYCUpload(file);
                      }
                      e.target.value = '';
                    }}
                    disabled={kycUploadState.isUploading || isSubmittingKYC || kycUploadState.currentUpload === null}
                    style={{ display: 'none' }}
                  />
                  <label 
                    htmlFor="kycUpload" 
                    className={`tenant-kyc-modal__upload-label ${kycUploadState.isUploading ? 'disabled' : ''}`}
                  >
                    {kycUploadState.isUploading ? (
                      <div>
                        <p style={{ margin: '10px 0', color: '#666' }}>Uploading...</p>
                      </div>
                    ) : (
                      <div>
                        <p style={{ margin: '10px 0', color: '#666' }}>
                          Click here to upload
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px', color: '#999' }}>
                          {kycUploadState.currentUpload === 'idFront' && 'Upload the front side of your ID'}
                          {kycUploadState.currentUpload === 'idBack' && 'Upload the back side of your ID'}
                          {kycUploadState.currentUpload === 'selfie' && 'Upload a clear selfie photo'}
                        </p>
                      </div>
                    )}
                  </label>
                </>
              )}
            </div>
            
            {kycUploadState.uploadedUrls.idFront && kycUploadState.currentUpload === 'idBack' && (
              <small className="tenant-kyc-modal__hint-success">
                ✓ ID Front uploaded successfully
              </small>
            )}
            {kycUploadState.uploadedUrls.idBack && kycUploadState.currentUpload === 'selfie' && (
              <small className="tenant-kyc-modal__hint-success">
                ✓ ID Front and ID Back uploaded successfully
              </small>
            )}
          </div>

          <div className="tenant-kyc-modal__actions">
            <button type="button" className="tenant-kyc-modal__btn tenant-kyc-modal__btn--secondary" onClick={onClose} disabled={isSubmittingKYC}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="tenant-kyc-modal__btn tenant-kyc-modal__btn--primary" 
              disabled={
                isSubmittingKYC || 
                kycUploadState.currentUpload !== null ||
                !kycUploadState.uploadedUrls.idFront ||
                !kycUploadState.uploadedUrls.idBack ||
                !kycUploadState.uploadedUrls.selfie
              }
            >
              {isSubmittingKYC ? 'Submitting...' : 'Submit KYC'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    portalRoot
  );
};

export default TenantKYCModal;
